// useStoreCourseData — Isomorphism Phase 3 hook
//
// Single source of truth: Zustand store. Context fallback removed.
// All sub-route components use this hook for course data + gate callbacks.
//
// Isomorphism mapping:
//   - Blind Spot 1 (field-level context isolation): Zustand slices enforce write boundaries
//   - tech-decisions.md §1 Layer 1 Kernel "State (单向数据流)"

import { useMemo } from 'react';
import { useStore } from '../store/index.js';
import type { CourseDraft, IngestPackage, CourseProgress } from '@examforge/shared/schemas';
import { deriveOutlineFromCasesV3 } from '@examforge/shared/schemas';

export interface StoreCourseData {
  draft: CourseDraft;
  pkg: IngestPackage;
  progress: CourseProgress;
  isStale: boolean;
  /** Refresh progress from IDB after writes. */
  refreshProgress: () => Promise<void>;
  /** Trigger regeneration (with confirmation dialog). */
  regenerate: () => Promise<void>;
  /** Switch to paste-JSON mode. */
  switchToPaste: () => void;
}

/**
 * Lazy normalization: backfill singular `caseId`/`topicId` from plural
 * `caseIds[0]`/`topicIds[0]` on every item. This is needed because:
 *   - IDB-stored data was parsed before the coerce fix (19BI Bug 1)
 *   - The coerce only runs during schema parse, not on IDB reads
 *   - Frontend code reads singular `caseId` everywhere
 * Idempotent — items that already have `caseId` pass through unchanged.
 * Mutates in place for performance (data is already a JS object from IDB).
 */
function normalizeItemTags(pkg: IngestPackage): IngestPackage {
  const collections: Array<Iterable<Record<string, unknown>>> = [
    pkg.flashcards as unknown as Record<string, unknown>[],
    pkg.mcqs as unknown as Record<string, unknown>[],
    (pkg.trueFalse ?? []) as unknown as Record<string, unknown>[],
    (pkg.essays ?? []) as unknown as Record<string, unknown>[],
    (pkg.mindmaps ?? []) as unknown as Record<string, unknown>[],
    (pkg.glossary ?? []) as unknown as Record<string, unknown>[],
    (pkg.lectureNotes ?? []) as unknown as Record<string, unknown>[],
    (pkg.freeResponses ?? []) as unknown as Record<string, unknown>[],
    (pkg.proofDrills ?? []) as unknown as Record<string, unknown>[],
  ];
  for (const coll of collections) {
    for (const item of coll) {
      if (item['caseId'] === undefined || item['caseId'] === null) {
        const ids = item['caseIds'];
        if (Array.isArray(ids) && ids.length > 0) {
          item['caseId'] = ids[0];
        }
      }
      if (item['topicId'] === undefined || item['topicId'] === null) {
        const ids = item['topicIds'];
        if (Array.isArray(ids) && ids.length > 0) {
          item['topicId'] = ids[0];
        }
      }
      if (item['conceptTag'] === undefined || item['conceptTag'] === null) {
        const ids = item['conceptIds'];
        if (Array.isArray(ids) && ids.length > 0) {
          item['conceptTag'] = ids[0];
        }
      }
    }
  }
  return pkg;
}

/**
 * Reads course data from Zustand store.
 *
 * Throws if called before CourseView populates the store (i.e. outside
 * a course sub-route). This is the correct behavior — components should
 * not render until CourseView has loaded the course.
 */
export function useStoreCourseData(): StoreCourseData {
  const storeDraft = useStore((s) => s.draft);
  const storePkg = useStore((s) => s.pkg);
  const storeProgress = useStore((s) => s.progress);
  const storeIsStale = useStore((s) => s.isStale);
  const storeRefreshProgress = useStore((s) => s.refreshProgress);
  const storeRegenerate = useStore((s) => s.regenerate);
  const storeSwitchToPaste = useStore((s) => s.switchToPaste);

  // Normalize on every render — cheap (array iteration) and idempotent.
  const normalizedPkg = useMemo(
    () => storePkg ? normalizeItemTags(storePkg) : null,
    // 19BM-C: normalizeItemTags is a module-level pure function, not a hook
    // dependency. Deps intentionally only [storePkg] — re-normalize on
    // reference change, nothing else.
    [storePkg],
  );

  // 19BI Bug 3 fix: bridge curriculumOutline from pkg to draft.
  // outline-view / refine-view read draft.curriculumOutline, but fresh v4 ingest
  // doesn't produce curriculumOutline on the IngestPackage (only migration/paste
  // paths do). When draft is missing it, derive from pkg.cases + topicHierarchy.
  // Idempotent — draft that already has curriculumOutline passes through unchanged.
  const enrichedDraft = useMemo(() => {
    if (!storeDraft || !normalizedPkg) return storeDraft;
    if (storeDraft.curriculumOutline) return storeDraft;

    // Source 1: pkg carries curriculumOutline (paste / IDB migration path)
    if (normalizedPkg['curriculumOutline']) {
      return { ...storeDraft, curriculumOutline: normalizedPkg['curriculumOutline'] };
    }

    // Source 2: derive from cases + topicHierarchy (fresh v4 ingest path)
    if (normalizedPkg.cases?.length > 0) {
      const derived = deriveOutlineFromCasesV3(normalizedPkg.cases, normalizedPkg.topicHierarchy);
      return { ...storeDraft, curriculumOutline: derived };
    }

    return storeDraft;
    // 19BM-C: deriveOutlineFromCasesV3 is a module-level pure import, not a
    // dependency. Deps intentionally [storeDraft, normalizedPkg] — these are
    // the only inputs to the derived outline.
  }, [storeDraft, normalizedPkg]);

  if (!storeDraft || !storePkg || !storeProgress) {
    throw new Error('useStoreCourseData called before CourseView populated the store');
  }
  if (!storeRefreshProgress || !storeRegenerate || !storeSwitchToPaste) {
    throw new Error('useStoreCourseData called before gate callbacks were set');
  }

  return {
    draft: enrichedDraft!,
    pkg: normalizedPkg!,
    progress: storeProgress,
    isStale: storeIsStale,
    refreshProgress: storeRefreshProgress,
    regenerate: storeRegenerate,
    switchToPaste: storeSwitchToPaste,
  };
}
